/**
 * ProtectedRoute — frontend route guard for authenticated / admin-only pages.
 *
 * Usage in App.tsx:
 *   <Route path="/AlbumEdit/:id" element={<ProtectedRoute><AlbumEdit /></ProtectedRoute>} />
 *   <Route path="/Admin"         element={<ProtectedRoute requireAdmin><Admin /></ProtectedRoute>} />
 *
 * Props:
 *   requireAdmin  (optional, default false)
 *     false → any authenticated user may access the route.
 *     true  → only users whose JWT role === 'admin' may access; others redirect to /.
 *
 * Behavior:
 *   - Unauthenticated visitors are redirected to /login with `state.from` so they
 *     return to the original URL after logging in.
 *   - Auth state comes from useAuth() (AuthContext), which decodes the JWT stored
 *     in localStorage.
 *
 * Currently protected routes (see App.tsx for the full list):
 *   requireAdmin:  /Admin
 *   authenticated: /AlbumCreate, /AlbumEdit/:id, /ArtistEdit/:id, /TrackEdit/:id,
 *                  /EditProfile/:id, /ArtistDashboard/:id, /SupporterDashboard,
 *                  /CreateArtistProfile, /CreateSupporterProfile,
 *                  /UploadTracks, /UploadSheetMusic, /UploadStem, /UploadPacks,
 *                  /UploadForm, /VideoUploader, /buskradio-create/:id, /buskradioedit/:id
 *
 * NOTE: This is a UI-level guard only. Ownership checks (e.g. "can this artist
 * edit *this* album?") must be enforced by the backend API.
 */
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** When true, only admin users may access the route. */
  requireAdmin?: boolean;
}

const ProtectedRoute = ({ children, requireAdmin = false }: ProtectedRouteProps) => {
  const location = useLocation();
  const { isAuthenticated, isAdmin } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
